import { Ioc } from "@core/ioc/ioc";
import { normalizeAliases } from "@models/data/aliasData";
import type { EntityData, EntitySnapshot } from "@models/data/entityData";
import { normalizeExplosionFactor } from "@models/data/explosionFactor";
import {
  isWireEntitySnapshot,
  mergeWireEntitySnapshot,
  wireSnapshotId,
} from "@models/data/entitySnapshotWire";
import {
  selectionSetActionFromWire,
  selectionSetFromWire,
} from "@models/data/selectionSetWire";
import { isOwnAppInstance } from "@models/multiuser/appInstanceId";
import type { IAnnotationsModel } from "@models/annotations/IAnnotationsModel";
import { ANNOTATIONS_MODEL } from "@models/annotations/IAnnotationsModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { IProjectListModel } from "@models/project/IProjectListModel";
import { PROJECT_LIST_MODEL } from "@models/project/IProjectListModel";
import type { IPermissionsModel } from "@models/permissions/IPermissionsModel";
import { PERMISSIONS_MODEL } from "@models/permissions/IPermissionsModel";
import type { ISharedUsersModel } from "@models/sharing/ISharedUsersModel";
import { SHARED_USERS_MODEL } from "@models/sharing/ISharedUsersModel";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";
import {
  CHANGE_SHARING_EVENT,
  changesProjectList,
  parseChangeSharingEvent,
  parseEvictUserEvent,
} from "@models/data/changeSharingWire";
import type { IEventModel, ServerEvent } from "./IEventModel";

/**
 * Routes server event envelopes into the same project apply methods used by
 * local edits. Event names intentionally accept a few casing variants because
 * Unity event constants have drifted across transports over time.
 *
 * Filtering mirrors Unity's `AWSServerEventListenerModel.IgnoreEvent`:
 *  - own-`aid` echoes are dropped (all handlers here reconcile REMOTE
 *    changes; the local paths already applied),
 *  - project-scoped events for a different (or no) active project are
 *    dropped — EXCEPT the project-list family (`update/create/delete_project`)
 *    which applies to the grid without an open project, and `load_scene`,
 *    whose roster/follow legs live in `MultiuserSessionModel`.
 */
export function wireProjectEventReconciliation(
  project: IProjectModel,
  events: IEventModel,
): () => void {
  return events.onEvent.on((event) => applyProjectEvent(project, event));
}

function permissionsModel(): IPermissionsModel | null {
  try {
    return Ioc.resolve<IPermissionsModel>(PERMISSIONS_MODEL);
  } catch {
    return null;
  }
}

function sharedUsersModel(): ISharedUsersModel | null {
  try {
    return Ioc.resolve<ISharedUsersModel>(SHARED_USERS_MODEL);
  } catch {
    return null;
  }
}

function currentUserId(): string | null {
  try {
    return Ioc.resolve<IAuthModel>(AUTH_MODEL).currentUser?.userId ?? null;
  } catch {
    return null;
  }
}

function refreshProjectList(): void {
  if (!Ioc.isRegistered<IProjectListModel>(PROJECT_LIST_MODEL)) return;
  void Ioc.resolve<IProjectListModel>(PROJECT_LIST_MODEL).refreshAsync().catch(() => undefined);
}

function applyProjectEvent(project: IProjectModel, event: ServerEvent): void {
  const type = event.type.toLowerCase();
  const payload = asRecord(event.payload);
  if (!payload) return;

  // -- Sharing changes: routed BEFORE the own-echo drop, deliberately ------
  //
  // ⚠ `change_sharing` and `evict_user` are on Unity's NEVER-IGNORE whitelist
  // (`AWSServerEventListenerModel.cs:215-216`), so the sender receives its own
  // event and FOUR of the five upstream consumers process it on purpose — to
  // refresh the sharer's own grid and share sheet. Only `PermissionsModel`
  // carries a sender guard, and it carries its own. Putting these two below
  // the blanket drop would silently break the sender's refresh.
  if (matches(type, "change_sharing", "changesharing")) {
    const evt = parseChangeSharingEvent(payload);
    if (!evt) return;
    permissionsModel()?.applyChangeSharing(evt);
    // `SharedUsersListViewModel.OnSharingChanged` reloads the open sheet and
    // `ParticipantsListViewModel.OnSharingChanged` refreshes its projection.
    // Local writes already emit synchronously from SharedUsersModel; ignore
    // this app instance's echo to avoid running the same Dynamo Query twice.
    if (!isOwnAppInstance(evt.originatingAppInstanceId)) {
      sharedUsersModel()?.notifySharingChanged(evt.projectId);
    }
    // Unity refreshes the grid for the SUBJECT, for any account-wide change,
    // and for the project owner (`ProjectsViewModel`). A refresh is cheap and
    // idempotent, so the union is safe.
    if (
      changesProjectList(evt.shareEvent)
      || evt.shareEvent === CHANGE_SHARING_EVENT.AccountRoleChanged
    ) {
      refreshProjectList();
    }
    return;
  }

  if (matches(type, "evict_user", "evictuser")) {
    const evt = parseEvictUserEvent(payload);
    if (!evt) return;
    const myUserId = currentUserId();
    if (!myUserId || evt.userId !== myUserId) return;
    // ⚠ TWO RECEIVERS, DIFFERENT STRICTNESS, and the asymmetry is upstream's:
    // the EDITOR requires both the user AND the project to match
    // (`EditorViewModel`), while the GRID checks the user only
    // (`ProjectsViewModel.cs:2856-2860`). Preserved rather than "fixed" — a
    // grid refresh is harmless, an unwanted eviction is not.
    if (evt.projectId && project.activeProjectId === evt.projectId) {
      project.applyActiveProjectDeleted(evt.projectId, "access-removed");
    }
    refreshProjectList();
    return;
  }

  // Own-echo drop (Unity compares `OriginatingAppInstanceId`).
  if (isOwnAppInstance(payload.aid ?? payload.OriginatingAppInstanceId)) return;

  // -- Project-list family: applies even with no / another project open --

  if (matches(type, "update_project")) {
    const projectId = stringField(payload, "projectId", "ProjectId");
    if (!projectId) return;
    if (boolField(payload, "hasConsoleAssetSettings", "HasConsoleAssetSettings") === true) {
      project.applyProjectConsoleAsset(
        projectId,
        stringField(payload, "consoleAssetId", "ConsoleAssetID"),
        stringArrayField(payload, "assetIds", "AssetIDs") ?? undefined,
      );
      return;
    }
    // Per-project user display settings ride the same event type but a
    // DIFFERENT flag (AI-925). Unity gates on `hasUserDisplaySettings` so a
    // rename (which leaves it false) can't zero the flags — and this leg
    // must run BEFORE the rename guard, since a settings event carries no
    // `projectName` at all.
    if (boolField(payload, "hasUserDisplaySettings", "HasUserDisplaySettings") === true) {
      project.applyProjectUserDisplaySettings(
        projectId,
        boolField(payload, "hideAvatars", "HideAvatars") === true,
        boolField(payload, "hideUserTags", "HideUserTags") === true,
      );
      return;
    }
    const projectName = stringField(payload, "projectName", "ProjectName");
    if (!projectName) return;
    project.applyProjectNameChange(projectId, projectName);
    if (Ioc.isRegistered<IProjectListModel>(PROJECT_LIST_MODEL)) {
      Ioc.resolve<IProjectListModel>(PROJECT_LIST_MODEL).applyProjectRename(projectId, projectName);
    }
    return;
  }

  // Folder events carry NO project id at all — Unity's four senders take a
  // `customerId` and never write it to the payload
  // (`AWSServerEventSenderModel.cs:257-308`) — so they belong up here with the
  // project-list family, ahead of the active-project scoping guard below.
  // Putting them after it would work today only by accident (the guard lets a
  // payload with no project id through) and would break the moment the guard
  // tightened.
  if (matches(type, "update_folder")) {
    if (!Ioc.isRegistered<IProjectListModel>(PROJECT_LIST_MODEL)) return;
    // `folderEvent` is an ORDINAL whose first member is Create = 0, so an
    // absent-vs-zero mix-up would silently turn every unrecognised payload into
    // a folder creation — test for the null explicitly rather than truthiness.
    const folderEvent = numberField(payload, "folderEvent", "FolderEventType");
    if (folderEvent === null || folderEvent === undefined) return;
    Ioc.resolve<IProjectListModel>(PROJECT_LIST_MODEL).applyFolderEventFromCloud(
      folderEvent,
      (payload as Record<string, unknown>).folder ??
        (payload as Record<string, unknown>).Folder,
    );
    return;
  }

  if (matches(type, "create_project", "delete_project")) {
    // Peers refetch the grid (Unity `ProjectsViewModel` refreshes off the
    // same events). Deleting the project someone is INSIDE evicts them to
    // the projects list (Unity `OnServerDeleteProject` parity) — announce
    // first so `ProjectView` navigates before the grid refresh races it.
    if (matches(type, "delete_project")) {
      const projectId = stringField(payload, "projectId", "ProjectId", "project_id");
      if (projectId) project.applyActiveProjectDeleted(projectId);
    }
    if (Ioc.isRegistered<IProjectListModel>(PROJECT_LIST_MODEL)) {
      void Ioc.resolve<IProjectListModel>(PROJECT_LIST_MODEL)
        .refreshAsync()
        .catch(() => undefined);
    }
    return;
  }

  // -- Everything below is scoped to the ACTIVE project --

  const eventProjectId = stringField(payload, "projectId", "ProjectId", "project_id");
  if (eventProjectId && project.activeProjectId && eventProjectId !== project.activeProjectId) {
    return;
  }
  if (eventProjectId && !project.activeProjectId) return;

  if (matches(type, "entitycreated", "entity.created", "createentity")) {
    const sceneId = stringField(payload, "sceneId", "SceneID", "sceneID", "SceneId");
    const entity = objectField<EntityData>(payload, "entity", "Entity");
    if (sceneId && entity) project.applyCreateEntity(sceneId, entity);
    return;
  }

  // A peer imported a model: the publisher sends only ids
  // (`ProjectModel.SendCreateEntityEvent`), so re-fetch parts + snapshots and
  // append. Idempotent, so a self-echo of our own publish is harmless.
  if (matches(type, "create_entity")) {
    const sceneId = stringField(payload, "sceneId", "SceneID", "sceneID", "SceneId");
    const assetId = stringField(payload, "assetId", "AssetID", "assetID", "AssetId");
    const entityId = stringField(payload, "entityId", "EntityID", "entityID", "EntityId");
    if (sceneId && assetId && entityId) {
      void project.applyCreateEntityFromCloud(sceneId, assetId, entityId);
    }
    return;
  }

  // Unity wire name is `delete_entity` (`EntityEventData`, tc 13 — ids
  // only, `assetId` omitted by the sender). Legacy web names tolerated.
  if (matches(type, "delete_entity", "entitydeleted", "entity.deleted", "deleteentity")) {
    const sceneId = stringField(payload, "sceneId", "SceneID", "sceneID", "SceneId");
    const entityId = stringField(payload, "entityId", "EntityID", "entityID", "EntityId");
    if (sceneId && entityId) project.applyDeleteEntity(sceneId, entityId);
    return;
  }

  if (
    matches(
      type,
      "entitysnapshotchanged",
      "entitysnapshotupdated",
      "entity.snapshot.changed",
      "entity.snapshot.updated",
      "update_entitysnapshot",
    )
  ) {
    const sceneId = stringField(payload, "sceneId", "SceneID", "sceneID", "SceneId");
    const wireSnapshot = objectField<Record<string, unknown>>(
      payload,
      "snapshot",
      "Snapshot",
      "entitySnapshot",
      "EntitySnapshot",
    );
    const snapshot =
      sceneId && wireSnapshot
        ? normalizeEntitySnapshot(project, sceneId, wireSnapshot)
        : null;
    if (sceneId && snapshot) project.applyEntitySnapshotChanges(sceneId, snapshot);
    return;
  }

  // A peer bulk-updated snapshots (`BulkEntitySnapshotEventData`, tc 55 —
  // wire fields `esids` + `updateEntityFlags` + `data`). Applied legs:
  // visibility (with the `isForcedVisibility` MODE SELECTOR — it picks
  // WHICH visibility field `data.visibility` writes, it is not a value),
  // lock (`isLocked` — note: different name than the snapshot's `isLock`),
  // constant spin, and persistent highlight. The pivot-based bulk TRANSFORM delta
  // (`ApplyEntityTransformChange`) is deliberately not ported — a Unity
  // multi-select move applies on reload (documented M12 deviation).
  if (matches(type, "bulk_update_entitysnapshot")) {
    const sceneId = stringField(payload, "sceneId", "SceneID", "SceneId");
    const snapshotIds = stringArrayField(payload, "esids", "EntitySnapshotIds");
    const data = objectField<Record<string, unknown>>(payload, "data", "Data");
    const flagMask = numberField(payload, "updateEntityFlags", "UpdateEntityFlags") ?? 0;
    if (!sceneId || !snapshotIds || !data) return;

    const hasSpin =
      boolField(data, "hasPersistentSpinUpdate", "HasPersistentSpinUpdate") === true;
    const rawJson = data.persistentSpinJson ?? data.PersistentSpinJson;
    const spinJson =
      typeof rawJson === "string" && rawJson.trim().length > 0 ? rawJson : null;
    const hasHighlight =
      boolField(data, "hasPersistentHighlightUpdate", "HasPersistentHighlightUpdate") === true;
    const rawHighlightJson = data.persistentHighlightJson ?? data.PersistentHighlightJson;
    const highlightJson =
      typeof rawHighlightJson === "string" && rawHighlightJson.trim().length > 0
        ? rawHighlightJson
        : null;
    const applyVisibility = (flagMask & (1 << 2)) !== 0;
    const forcedMode = boolField(data, "isForcedVisibility", "IsForcedVisibility") === true;
    const visibilityValue = boolField(data, "visibility", "Visibility");
    const applyLock = (flagMask & (1 << 9)) !== 0;
    const lockValue = boolField(data, "isLocked", "IsLocked");

    for (const snapshotId of snapshotIds) {
      const current = project.getEntitySnapshotById(sceneId, snapshotId);
      if (!current) continue;
      let next = current;
      if (applyVisibility && visibilityValue !== null) {
        next = forcedMode
          ? { ...next, forcedVisibility: visibilityValue }
          : { ...next, visibility: visibilityValue };
      }
      if (applyLock && lockValue !== null) {
        next = { ...next, isLocked: lockValue };
      }
      if (hasSpin && next.persistentSpinJson !== spinJson) {
        next = { ...next, persistentSpinJson: spinJson };
      }
      if (hasHighlight && next.persistentHighlightJson !== highlightJson) {
        next = { ...next, persistentHighlightJson: highlightJson };
      }
      if (next !== current) project.applyEntitySnapshotChanges(sceneId, next);
    }
    return;
  }

  // -- Media tiles (Unity `KnownEventTypes` + `MediaTileEventData.cs` wire
  //    field names: `mt`, `mtid`, `mtids`, `data`, `updateEntityFlags`) --

  if (matches(type, "create_mediatile")) {
    const sceneId = stringField(payload, "sceneId", "SceneID", "SceneId");
    const wireTile = objectField<Record<string, unknown>>(payload, "mt", "MediaTileData", "mediaTileData");
    if (sceneId && wireTile) void project.applyCreateMediaTile(sceneId, wireTile);
    return;
  }

  if (matches(type, "delete_mediatile")) {
    const sceneId = stringField(payload, "sceneId", "SceneID", "SceneId");
    const tileId = stringField(payload, "mtid", "MediaTileId", "mediaTileId");
    if (sceneId && tileId) project.applyDeleteMediaTile(sceneId, tileId);
    return;
  }

  if (matches(type, "update_mediatile")) {
    const sceneId = stringField(payload, "sceneId", "SceneID", "SceneId");
    const tileIds = stringArrayField(payload, "mtids", "MediaTileIDs", "mediaTileIds");
    const wireData = objectField<Record<string, unknown>>(payload, "data", "Data");
    const flagMask = numberField(payload, "updateEntityFlags", "UpdateEntityFlags");
    if (sceneId && tileIds && wireData && flagMask !== null) {
      project.applyMediaTileChanges(sceneId, tileIds, wireData, flagMask);
    }
    return;
  }

  if (matches(type, "update_mediaasset")) {
    const sceneId = stringField(payload, "sceneId", "SceneID", "SceneId");
    const tileId = stringField(payload, "mtid", "MediaTileId", "mediaTileId");
    if (sceneId && tileId) void project.applyMediaAssetChange(sceneId, tileId);
    return;
  }

  // A peer updated the scene row (`SceneEventData` — full JsonUtility
  // `SceneData` under `sceneData`). Full replace, mirroring Unity's
  // `SceneInfo.Set` + the `ApplyMoveScene` index leg. The cutting-plane
  // fields ride the same apply (they're part of `SceneData`), so the old
  // cutting-plane-only handler is superseded.
  if (matches(type, "update_scene")) {
    const sceneId = stringField(payload, "sceneId", "SceneID", "SceneId");
    const sceneData = objectField<Record<string, unknown>>(payload, "sceneData", "SceneData");
    if (sceneId && sceneData) project.applySceneChangesFromWire(sceneId, sceneData);
    return;
  }

  // A peer created a scene (also sent at the END of a scene duplication —
  // there is no `duplicate_scene` event type; the rows are already
  // persisted when this arrives).
  if (matches(type, "create_scene")) {
    const sceneData = objectField<Record<string, unknown>>(payload, "sceneData", "SceneData");
    if (sceneData) project.applyCreateSceneFromWire(sceneData);
    return;
  }

  if (matches(type, "delete_scene")) {
    const sceneId = stringField(payload, "sceneId", "SceneID", "SceneId");
    if (sceneId) project.applyDeleteScene(sceneId);
    return;
  }

  // A peer re-rendered a scene thumbnail (`TargetSceneEventData`, tc 39 —
  // notification only, the blob is refetched).
  if (matches(type, "update_thumbnail")) {
    const sceneId = stringField(payload, "sceneId", "SceneID", "SceneId");
    if (sceneId) project.applySceneThumbnailUpdate(sceneId);
    return;
  }

  // A peer edited a material (`MaterialEventData`, tc 23 — full JsonUtility
  // `MaterialData` payload; the event has NO sceneId by design).
  if (matches(type, "update_material")) {
    const materialId = stringField(payload, "materialId", "MaterialId");
    const materialData = objectField<Record<string, unknown>>(payload, "materialData", "MaterialData");
    if (materialId && materialData) project.applyMaterialChanges(materialId, materialData);
    return;
  }

  // -- Annotations (`AnnotationEventData` tc 2 for create + edit,
  //    `DeleteAnnotationEventData` tc 10). The envelope's `sceneId` is
  //    always "" upstream — the scene lives inside `update.a.SceneID`, so
  //    the model locates rows by annotation id.

  if (matches(type, "create_annotation", "edit_annotation")) {
    if (!Ioc.isRegistered<IAnnotationsModel>(ANNOTATIONS_MODEL)) return;
    const update = objectField<Record<string, unknown>>(payload, "update", "Update");
    if (!update) return;
    const wire = objectField<Record<string, unknown>>(update, "a", "Annotation");
    if (!wire) return;
    const annotations = Ioc.resolve<IAnnotationsModel>(ANNOTATIONS_MODEL);
    if (matches(type, "create_annotation")) {
      annotations.applyAnnotationCreatedFromCloud(wire);
    } else {
      annotations.applyAnnotationEditedFromCloud(
        numberField(update, "flags", "Flags") ?? 0,
        stringField(update, "rId", "ReplyId") ?? "",
        wire,
      );
    }
    return;
  }

  if (matches(type, "delete_annotation")) {
    if (!Ioc.isRegistered<IAnnotationsModel>(ANNOTATIONS_MODEL)) return;
    const annotationId = stringField(payload, "annotationId", "AnnotationId");
    if (annotationId) {
      Ioc.resolve<IAnnotationsModel>(ANNOTATIONS_MODEL)
        .applyAnnotationDeletedFromCloud(annotationId);
    }
    return;
  }

  // Selection set create/update/delete (`SelectionSetEventData`, tc 56 —
  // ONE event type; `action` int picks the operation, `flags` int masks
  // the update legs).
  if (matches(type, "update_selectionset")) {
    const sceneId = stringField(payload, "sceneId", "SceneID", "SceneId");
    const action = selectionSetActionFromWire(payload.action ?? payload.Action);
    const wireData = objectField<Record<string, unknown>>(
      payload,
      "data",
      "Data",
      "selectionSet",
      "SelectionSet",
    );
    if (!sceneId || !action || !wireData) return;
    const data = selectionSetFromWire(wireData);
    if (!data) return;
    const flagMask =
      numberField(payload, "flags", "Flags", "UpdateFlags", "updateFlagsMask") ?? 0;
    project.applySelectionSetEvent(sceneId, action, flagMask, data);
    return;
  }
}

function boolField(
  value: Record<string, unknown>,
  ...keys: ReadonlyArray<string>
): boolean | null {
  for (const key of keys) {
    const field = value[key];
    if (typeof field === "boolean") return field;
  }
  return null;
}

function normalizeEntitySnapshot(
  project: IProjectModel,
  sceneId: string,
  value: Record<string, unknown>,
): EntitySnapshot | null {
  const snapshotId = stringField(
    value,
    "id",
    "snapshotID",
    "SnapshotID",
    "partID",
    "PartID",
  );
  if (!snapshotId) return null;

  const explosionFactor =
    numberField(value, "explosionFactor", "ExplosionFactor") ?? undefined;
  // Constant-spin payload (`persistentSpinJson` on both the web + Unity
  // wire shapes; blank = no spin, absent = untouched).
  const rawSpin = value.persistentSpinJson ?? value.PersistentSpinJson;
  const persistentSpinJson =
    typeof rawSpin === "string"
      ? (rawSpin.trim().length > 0 ? rawSpin : null)
      : undefined;
  const rawHighlight = value.persistentHighlightJson ?? value.PersistentHighlightJson;
  const persistentHighlightJson =
    typeof rawHighlight === "string"
      ? (rawHighlight.trim().length > 0 ? rawHighlight : null)
      : undefined;

  if (
    typeof value.id === "string"
    && value.transform
    && typeof value.transform === "object"
    && Array.isArray(value.children)
  ) {
    const snapshot = value as unknown as EntitySnapshot;
    return {
      ...snapshot,
      explosionFactor: normalizeExplosionFactor(
        explosionFactor ?? snapshot.explosionFactor,
      ),
      persistentSpinJson:
        persistentSpinJson === undefined
          ? (snapshot.persistentSpinJson ?? null)
          : persistentSpinJson,
      persistentHighlightJson:
        persistentHighlightJson === undefined
          ? (snapshot.persistentHighlightJson ?? null)
          : persistentHighlightJson,
      // `EntitySnapshot.aliases` is non-optional (`[]` = none), but this branch
      // spreads an UNTRUSTED payload: a web peer on a build that predates part
      // name aliases sends no `aliases` at all, and the raw spread would put
      // `undefined` on a field every consumer dereferences. Normalizing also
      // drops malformed entries rather than letting them reach the editor.
      aliases: normalizeAliases(
        Array.isArray(snapshot.aliases) ? snapshot.aliases : [],
      ),
    };
  }

  // Unity JsonUtility shape (`EntitySnapshotData` — `snapshotID`,
  // `[{name,val}]` pair-array transforms, `isLock`, euler rotations):
  // merge the full modeled surface onto the current snapshot so a peer's
  // move / rename / hide / lock / material change applies live.
  if (isWireEntitySnapshot(value)) {
    const wireId = wireSnapshotId(value) ?? snapshotId;
    const current = project.getEntitySnapshotById(sceneId, wireId);
    if (!current) return null;
    return mergeWireEntitySnapshot(value, current);
  }

  const current = project.getEntitySnapshotById(sceneId, snapshotId);
  if (!current) return null;
  if (
    explosionFactor === undefined
    && persistentSpinJson === undefined
    && persistentHighlightJson === undefined
  ) {
    return current;
  }
  return {
    ...current,
    explosionFactor:
      explosionFactor === undefined
        ? current.explosionFactor
        : normalizeExplosionFactor(explosionFactor),
    persistentSpinJson:
      persistentSpinJson === undefined ? current.persistentSpinJson : persistentSpinJson,
    persistentHighlightJson:
      persistentHighlightJson === undefined
        ? current.persistentHighlightJson
        : persistentHighlightJson,
  };
}

function matches(type: string, ...names: string[]): boolean {
  return names.some((name) => type === name.toLowerCase());
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? value as Record<string, unknown> : null;
}

function stringField(
  value: Record<string, unknown>,
  ...keys: ReadonlyArray<string>
): string | null {
  for (const key of keys) {
    const field = value[key];
    if (typeof field === "string" && field.length > 0) return field;
  }
  return null;
}

function objectField<T>(
  value: Record<string, unknown>,
  ...keys: ReadonlyArray<string>
): T | null {
  for (const key of keys) {
    const field = value[key];
    if (field && typeof field === "object") return field as T;
  }
  return null;
}

function stringArrayField(
  value: Record<string, unknown>,
  ...keys: ReadonlyArray<string>
): ReadonlyArray<string> | null {
  for (const key of keys) {
    const field = value[key];
    if (Array.isArray(field) && field.every((item) => typeof item === "string")) {
      return field as string[];
    }
  }
  return null;
}

function numberField(
  value: Record<string, unknown>,
  ...keys: ReadonlyArray<string>
): number | null {
  for (const key of keys) {
    const field = value[key];
    if (typeof field === "number" && Number.isFinite(field)) return field;
    if (typeof field === "string") {
      const parsed = Number.parseInt(field, 10);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}
